/**
 * Canonical money handling for the entire DibsOnDance codebase.
 *
 * Wire format (what the BE serializes — every host + portal API):
 *   - `unitAmount` is an INTEGER in MINOR UNITS (cents/grosze): 2500 == 25.00.
 *   - `currency` is a LOWERCASE ISO 4217 code: "pln", "eur".
 *
 * Form / UI format (what users see and type):
 *   - amount is a DECIMAL number (25 or 25.50).
 *   - currency is UPPERCASE ('PLN', 'EUR') for display.
 *
 * Conversion is centralized here so features never reinvent the wheel —
 * Bilety, Addons, Coupons, Checkout, Sales, Host Financials all funnel
 * through `toWireMoney` / `fromWireMoney`. See
 * `.agent-os/standards/money-handling.md` for the project rule.
 */

export interface WireMoney {
  /** Integer minor units (cents/grosze): 2500 == 25.00. */
  unitAmount: number;
  /** Lowercase ISO 4217 code: "pln", "eur". */
  currency: string;
}

export interface FormMoney {
  /** Decimal amount as shown to the user: 25 or 25.50. */
  amount: number;
  /** Uppercase ISO 4217 code for display: "PLN", "EUR". */
  currency: string;
}

/**
 * Form decimal → wire minor units + lowercase currency.
 *
 * `Math.round` guards against floating-point trailing digits — e.g.
 * `25.50 * 100 === 2549.9999999999995` on some runtimes.
 *
 * Null/undefined amount is treated as 0 (caller decides whether 0 is a
 * valid value via validators; this helper only does the conversion).
 */
export function toWireMoney(
  amount: number | null | undefined,
  currency: string
): WireMoney {
  return {
    unitAmount: Math.round(Number(amount ?? 0) * 100),
    currency: (currency ?? '').toLowerCase(),
  };
}

/**
 * Wire minor units + lowercase → form decimal + uppercase currency.
 */
export function fromWireMoney(wire: WireMoney): FormMoney {
  return {
    amount: (wire?.unitAmount ?? 0) / 100,
    currency: (wire?.currency ?? '').toUpperCase(),
  };
}

/**
 * DOD-606 — the ×100 / ÷100 conversion on its own, with no currency involved.
 *
 * Some call sites only ever want the number: a card formatting an amount whose
 * currency the surrounding template already prints, or a mapper turning a form
 * decimal into minor units before the currency is attached elsewhere. Those
 * used to pass a literal `'pln'` to `toWireMoney` / `fromWireMoney` and throw
 * the currency away — harmless while every supported currency uses a factor of
 * 100, but it reads as a złoty assumption and would be a real bug the day one
 * does not.
 *
 * Use these when there is genuinely no currency in play. When there is one,
 * use `toWireMoney` / `fromWireMoney` and carry it.
 */
export function decimalToMinorUnits(amount: number | null | undefined): number {
  // Same `Math.round` guard as `toWireMoney`: 25.50 * 100 can land on
  // 2549.9999999999995.
  return Math.round(Number(amount ?? 0) * 100);
}

/** Minor units → decimal. The inverse of `decimalToMinorUnits`. */
export function minorUnitsToDecimal(unitAmount: number | null | undefined): number {
  return (unitAmount ?? 0) / 100;
}
